import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface RouteManagementProps {
  domain: string;
}

interface Route {
  id: string;
  routeName: string;
  routeNumber: string;
  startPoint: string;
  endPoint: string;
  totalDistance: string;
  estimatedTime: string;
  stops: string[];
  assignedVehicle: string;
  assignedDriver: string;
  status: 'Active' | 'Inactive';
}

const RouteManagement: React.FC<RouteManagementProps> = ({ domain }) => {
  const [routes, setRoutes] = useState<Route[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRoute, setEditingRoute] = useState<Route | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentStop, setCurrentStop] = useState('');
  const [formData, setFormData] = useState({
    routeName: '',
    routeNumber: '',
    startPoint: '',
    endPoint: '',
    totalDistance: '',
    estimatedTime: '',
    stops: [] as string[],
    assignedVehicle: '',
    assignedDriver: '',
    status: 'Active' as 'Active' | 'Inactive',
  });

  useEffect(() => {
    fetchRoutes();
  }, [domain]);

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const routesRef = collection(db, `school-users/${domain}/transport-routes`);
      const snapshot = await getDocs(routesRef);
      const routeList: Route[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Route));
      setRoutes(routeList);
    } catch (error) {
      console.error('Error fetching routes:', error);
      alert('Failed to fetch routes');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddStop = () => {
    if (currentStop.trim()) {
      setFormData(prev => ({
        ...prev,
        stops: [...prev.stops, currentStop.trim()]
      }));
      setCurrentStop('');
    }
  };

  const handleRemoveStop = (index: number) => {
    setFormData(prev => ({
      ...prev,
      stops: prev.stops.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.routeName || !formData.routeNumber || !formData.startPoint || !formData.endPoint) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const routeData = { ...formData };

      if (editingRoute) {
        const routeRef = doc(db, `school-users/${domain}/transport-routes`, editingRoute.id);
        await updateDoc(routeRef, routeData);
        alert('Route updated successfully!');
      } else {
        await addDoc(collection(db, `school-users/${domain}/transport-routes`), routeData);
        alert('Route added successfully!');
      }

      resetForm();
      fetchRoutes();
    } catch (error) {
      console.error('Error saving route:', error);
      alert('Failed to save route');
    }
  };

  const handleEdit = (route: Route) => {
    setEditingRoute(route);
    setFormData({
      routeName: route.routeName,
      routeNumber: route.routeNumber,
      startPoint: route.startPoint,
      endPoint: route.endPoint,
      totalDistance: route.totalDistance,
      estimatedTime: route.estimatedTime,
      stops: route.stops || [],
      assignedVehicle: route.assignedVehicle,
      assignedDriver: route.assignedDriver,
      status: route.status,
    });
    setShowForm(true);
  };

  const handleDelete = async (routeId: string) => {
    if (!confirm('Are you sure you want to delete this route?')) return;

    try {
      await deleteDoc(doc(db, `school-users/${domain}/transport-routes`, routeId));
      alert('Route deleted successfully!');
      fetchRoutes();
    } catch (error) {
      console.error('Error deleting route:', error);
      alert('Failed to delete route');
    }
  };

  const resetForm = () => {
    setFormData({
      routeName: '',
      routeNumber: '',
      startPoint: '',
      endPoint: '',
      totalDistance: '',
      estimatedTime: '',
      stops: [],
      assignedVehicle: '',
      assignedDriver: '',
      status: 'Active',
    });
    setEditingRoute(null);
    setShowForm(false);
    setCurrentStop('');
  };

  const filteredRoutes = routes.filter(route =>
    route.routeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    route.routeNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    route.startPoint.toLowerCase().includes(searchTerm.toLowerCase()) ||
    route.endPoint.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          <i className="bi bi-map me-2"></i>Route Management
        </h5>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          <i className="bi bi-plus-circle me-2"></i>
          {showForm ? 'Cancel' : 'Add Route'}
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <h6 className="card-title mb-3">
              {editingRoute ? 'Edit Route' : 'Add New Route'}
            </h6>
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Route Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="routeName"
                    value={formData.routeName}
                    onChange={handleInputChange}
                    placeholder="e.g., North Delhi Route"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Route Number *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="routeNumber"
                    value={formData.routeNumber}
                    onChange={handleInputChange}
                    placeholder="e.g., R-01"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Start Point *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="startPoint"
                    value={formData.startPoint}
                    onChange={handleInputChange}
                    placeholder="e.g., School Campus"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">End Point *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="endPoint"
                    value={formData.endPoint}
                    onChange={handleInputChange}
                    placeholder="e.g., Rohini Sector 10"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Total Distance (km)</label>
                  <input
                    type="text"
                    className="form-control"
                    name="totalDistance"
                    value={formData.totalDistance}
                    onChange={handleInputChange}
                    placeholder="e.g., 15"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Estimated Time</label>
                  <input
                    type="text"
                    className="form-control"
                    name="estimatedTime"
                    value={formData.estimatedTime}
                    onChange={handleInputChange}
                    placeholder="e.g., 45 mins"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Assigned Vehicle</label>
                  <input
                    type="text"
                    className="form-control"
                    name="assignedVehicle"
                    value={formData.assignedVehicle}
                    onChange={handleInputChange}
                    placeholder="e.g., DL-1234"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Assigned Driver</label>
                  <input
                    type="text"
                    className="form-control"
                    name="assignedDriver"
                    value={formData.assignedDriver}
                    onChange={handleInputChange}
                    placeholder="e.g., Ramesh Kumar"
                  />
                </div>
                <div className="col-md-12">
                  <label className="form-label">Route Stops</label>
                  <div className="input-group mb-2">
                    <input
                      type="text"
                      className="form-control"
                      value={currentStop}
                      onChange={(e) => setCurrentStop(e.target.value)}
                      placeholder="Enter stop name"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddStop())}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleAddStop}
                    >
                      <i className="bi bi-plus-circle me-1"></i>Add Stop
                    </button>
                  </div>
                  {formData.stops.length > 0 && (
                    <div className="d-flex flex-wrap gap-2">
                      {formData.stops.map((stop, index) => (
                        <span key={index} className="badge bg-primary d-flex align-items-center gap-1">
                          {index + 1}. {stop}
                          <i
                            className="bi bi-x-circle"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleRemoveStop(index)}
                          ></i>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="mt-3">
                <button type="submit" className="btn btn-primary me-2">
                  <i className="bi bi-check-circle me-2"></i>
                  {editingRoute ? 'Update Route' : 'Add Route'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  <i className="bi bi-x-circle me-2"></i>Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="card mb-3 shadow-sm">
        <div className="card-body">
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by route name, number, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Routes Grid */}
      <div className="row g-3">
        {loading ? (
          <div className="col-12 text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : filteredRoutes.length === 0 ? (
          <div className="col-12 text-center py-5 text-muted">
            <i className="bi bi-map fs-1 d-block mb-3"></i>
            <p>No routes found</p>
          </div>
        ) : (
          filteredRoutes.map((route) => (
            <div key={route.id} className="col-md-6 col-lg-4">
              <div className="card h-100 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h6 className="card-title mb-1">{route.routeName}</h6>
                      <span className="badge bg-primary">{route.routeNumber}</span>
                    </div>
                    <span className={`badge ${route.status === 'Active' ? 'bg-success' : 'bg-secondary'}`}>
                      {route.status}
                    </span>
                  </div>
                  <div className="mb-2">
                    <small className="text-muted d-block">
                      <i className="bi bi-geo-alt me-1"></i>
                      <strong>From:</strong> {route.startPoint}
                    </small>
                    <small className="text-muted d-block">
                      <i className="bi bi-geo-alt-fill me-1"></i>
                      <strong>To:</strong> {route.endPoint}
                    </small>
                  </div>
                  <div className="mb-2">
                    <small className="text-muted d-block">
                      <i className="bi bi-speedometer2 me-1"></i>
                      <strong>Distance:</strong> {route.totalDistance || 'N/A'}
                    </small>
                    <small className="text-muted d-block">
                      <i className="bi bi-clock me-1"></i>
                      <strong>Time:</strong> {route.estimatedTime || 'N/A'}
                    </small>
                  </div>
                  {route.stops && route.stops.length > 0 && (
                    <div className="mb-2">
                      <small className="text-muted d-block mb-1">
                        <i className="bi bi-signpost-2 me-1"></i>
                        <strong>Stops ({route.stops.length}):</strong>
                      </small>
                      <div className="d-flex flex-wrap gap-1">
                        {route.stops.slice(0, 3).map((stop, idx) => (
                          <span key={idx} className="badge bg-light text-dark" style={{ fontSize: '0.7rem' }}>
                            {stop}
                          </span>
                        ))}
                        {route.stops.length > 3 && (
                          <span className="badge bg-light text-dark" style={{ fontSize: '0.7rem' }}>
                            +{route.stops.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="mb-3">
                    {route.assignedVehicle && (
                      <small className="text-muted d-block">
                        <i className="bi bi-truck me-1"></i>
                        <strong>Vehicle:</strong> {route.assignedVehicle}
                      </small>
                    )}
                    {route.assignedDriver && (
                      <small className="text-muted d-block">
                        <i className="bi bi-person me-1"></i>
                        <strong>Driver:</strong> {route.assignedDriver}
                      </small>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-primary flex-fill"
                      onClick={() => handleEdit(route)}
                    >
                      <i className="bi bi-pencil me-1"></i>Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(route.id)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RouteManagement;
